import { createContext, Dispatch, SetStateAction, useContext } from "react";
import { BatchContractSweepPreviewParams } from "./FormContext";
import { WalletSmartAccountType } from "@wallet-manager/node-types/dist/src/postgres/const";

export type BatchContractSweepSubmitParams = {
  assetName: string;
  spenderAddress: string;
  chainType: string;
  chainId: string;
  riskType: string;
  walletVersion: string;
  sweepRecords: {
    fromAddress: string;
    amount: string;
    decimals: number;
  }[];
  tronFreeBandwidthOnly?: boolean;
  smartAccountType?: WalletSmartAccountType;
};

export type BatchContractSweepPreviewAddressData = {
  wallet_address: string;
  path: string;
  from_address: string;
  to_address: string;
  amount: string;
  decimals: number;
  wallet_version: number;
  risk_level: number;
  no_risk_level_count: number;
  wallet_settlement_date: string;
  from_auto_sweep_pending_queue: boolean;
};

export type BatchContractSweepPreviewData = {
  previewParams: BatchContractSweepPreviewParams;
  addressData: BatchContractSweepPreviewAddressData[];
};

export type SendingAddressMap = Map<
  string,
  {
    walletVersion: number;
    sweepAmount: string;
    sweepableAmount: string;
    riskLevel: number;
    noRiskLevelCount: number;
    isPendingQueue: boolean;
  }
>;

export type BatchContractSweepPreviewContextType = {
  previewData: BatchContractSweepPreviewData | null;
  submitParams: BatchContractSweepSubmitParams;
  onSubmit: () => void;
  onReset: () => void;
  sendingAddressesMap: SendingAddressMap;
  setSendingAddressesMap: Dispatch<SetStateAction<SendingAddressMap>>;
  toDisplayAmountWithCurrency: (amount: string | number) => string;
  summaryDetails: {
    addressesSummary: {
      totalCount: number;
      selectedCount: number;
      totalSweepAmount: string;
      totalSweepableAmount: string;
    };
    spenderAddress: string;
    currency: string;
    chainName: string;
  };
  translate: (key: string, params?: Record<string, any>) => string;
};

export const BatchContractSweepPreviewContext =
  createContext<BatchContractSweepPreviewContextType | null>(null);

export const useBatchContractSweepPreviewContext = () => {
  const context = useContext(BatchContractSweepPreviewContext);

  if (!context) {
    throw new Error(
      "useBatchContractSweepPreviewContext must be used within a BatchContractSweepContextProvider",
    );
  }

  return context;
};
